// Landing page commerciale — ExecPro360
import { useState } from "react";
import Chatbot from "../components/Chatbot";
import DemoRequestModal from "../components/DemoRequestModal";
import { PUBLISHER_LABEL, COPYRIGHT_YEAR } from "../config/legal";
import { PLAN_FEATURES } from "../config/planFeatures";

const logoIcon = "/assets/logo-icon-execpro360.png";
const logoLockup = "/assets/logo-execpro360-lockup.png";

const PLANS = [
  {
    key: 'essentiel',
    name: 'Essentiel',
    tagline: 'Facturation et pilotage financier structurés',
    priceMonthly: 49,
    priceAnnual: 39,
    users: '1 utilisateur',
    recommended: false,
    features: PLAN_FEATURES.essentiel.features,
  },
  {
    key: 'business',
    name: 'Business',
    tagline: 'La plateforme opérationnelle complète pour PME',
    priceMonthly: 129,
    priceAnnual: 99,
    users: "Jusqu'à 5 utilisateurs",
    recommended: true,
    inherits: PLAN_FEATURES.business.inherits,
    features: PLAN_FEATURES.business.features,
  },
  {
    key: 'ultimate',
    name: 'Ultimate',
    tagline: 'Pilotage multi-sociétés et contrôle avancé',
    priceMonthly: 279,
    priceAnnual: 199,
    users: "Jusqu'à 25 utilisateurs · 1 société satellite incluse",
    recommended: false,
    inherits: PLAN_FEATURES.ultimate.inherits,
    features: PLAN_FEATURES.ultimate.features,
  },
];

const SATELLITE_ADDON = { monthly: 49, annual: 39 };

// Prix annuel affiché « par mois » → total réellement facturé une fois par an
// (même calcul que les tarifs Stripe : scripts/stripe-copy-prices.js).
const annualTotal = perMonth => (perMonth * 12).toLocaleString('fr-FR').replace(/ /g, ' ');
const LOWEST_ANNUAL = Math.min(...PLANS.map(p => p.priceAnnual));

const FEATURES = [
  { icon: 'document', title: 'Facturation', desc: 'Créez et envoyez des factures professionnelles en quelques secondes. Relances automatiques incluses.' },
  { icon: 'bank', title: 'Banque & Trésorerie', desc: 'Importez vos relevés bancaires et visualisez vos flux. Réconciliation assistée.' },
  { icon: 'box', title: 'Stocks', desc: 'Gérez votre inventaire avec traçabilité par lot, alertes de réapprovisionnement et transferts inter-sites.' },
  { icon: 'users', title: 'CRM intégré', desc: 'Pipeline commercial, suivi des prospects et opportunités, activités planifiées — tout dans ExecPro360.' },
  { icon: 'cpu', title: 'Intelligence artificielle', desc: 'Lecture automatique des factures, assistant, rapport mensuel, analyse budgétaire et prospection, par Mistral AI (France) — inclus dans vos modules, sans configuration.' },
  { icon: 'chart', title: 'Comptabilité & FEC', desc: 'Plan comptable, journaux, FEC exportable, TVA intracommunautaire et accès expert-comptable intégré.' },
];

// ── Icônes ligne, dessinées à la main (pas d'emoji) ──────────────
const ICON_PATHS = {
  document: <><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M9.5 12h6M9.5 15.5h6M9.5 8.5h2" /></>,
  bank: <><path d="M4 10l8-5 8 5" /><path d="M5 10v9M9 10v9M15 10v9M19 10v9" /><path d="M3 19h18" /></>,
  box: <><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" /><path d="M4 7.5l8 4.5 8-4.5" /><path d="M12 12v9" /></>,
  users: <><circle cx="9" cy="8" r="3" /><path d="M3.2 20c0-3.3 2.6-6 5.8-6s5.8 2.7 5.8 6" /><circle cx="17.2" cy="9.2" r="2.3" /><path d="M15.8 14.3c2.4.5 4.1 2.5 4.1 5" /></>,
  cpu: <><rect x="7.5" y="7.5" width="9" height="9" rx="1.6" /><path d="M9.5 3.2v3M14.5 3.2v3M9.5 17.8v3M14.5 17.8v3M3.2 9.5h3M3.2 14.5h3M17.8 9.5h3M17.8 14.5h3" /></>,
  chart: <><path d="M4 20V10M10 20V4M16 20v-7M4 20h16" /></>,
  calendar: <><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 9.5h17M8 3v4M16 3v4" /></>,
  refresh: <><path d="M4.5 11a7.5 7.5 0 0113-5M4.5 5.5v5.5h5.5" /><path d="M19.5 13a7.5 7.5 0 01-13 5M19.5 18.5V13H14" /></>,
  briefcase: <><rect x="3.5" y="8" width="17" height="11" rx="2" /><path d="M8.5 8V6a2 2 0 012-2h3a2 2 0 012 2v2" /><path d="M3.5 13h17" /></>,
  folder: <><path d="M3.5 7a2 2 0 012-2h4l2 2h7a2 2 0 012 2v8a2 2 0 01-2 2h-13a2 2 0 01-2-2z" /></>,
  trending: <><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></>,
  target: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /></>,
  userCheck: <><circle cx="9.5" cy="8" r="3.2" /><path d="M3.5 20c0-3.4 2.7-6 6-6" /><path d="M14.5 13.2l2 2 3.5-4" /></>,
  shield: <><path d="M12 3l7 3v6c0 4.8-3 8.2-7 9-4-.8-7-4.2-7-9V6z" /><path d="M9 12l2 2 4-4.2" /></>,
  eye: <><path d="M2.2 12S5.6 5.8 12 5.8 21.8 12 21.8 12 18.4 18.2 12 18.2 2.2 12 2.2 12z" /><circle cx="12" cy="12" r="2.8" /></>,
  award: <><circle cx="12" cy="8" r="4.6" /><path d="M8.7 12.3L7.3 21l4.7-2.6 4.7 2.6-1.4-8.7" /></>,
  percent: <><path d="M6 18L18 6" /><circle cx="7.5" cy="7.5" r="2.1" /><circle cx="16.5" cy="16.5" r="2.1" /></>,
  spark: <><path d="M12 2.5l1.8 5.7 5.7 1.8-5.7 1.8L12 17.5l-1.8-5.7-5.7-1.8 5.7-1.8z" /></>,
  arrowUpRight: <><path d="M7 17L17 7M9 7h8v8" /></>,
};

function Icon({ name, size = 20, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={style}>
      {ICON_PATHS[name]}
    </svg>
  );
}

export default function Landing({ onGoToLogin, onGoToRegister }) {
  const goToRegister = onGoToRegister || onGoToLogin;
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [partnerForm, setPartnerForm] = useState({ name: '', email: '', cabinet: '', clients: '' });
  const [partnerSent, setPartnerSent] = useState(false);

  return (
    <div className="pp-landing" style={{ background: "var(--ink)", minHeight: "100vh", color: "var(--text)", fontFamily: "'Inter', system-ui, sans-serif", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@500;700;800&family=Inter:wght@400;500;600&display=swap');

        /* Scopé sous .pp-landing (et non :root) : le thème du dashboard
           (src/styles/themes.js) pose ses propres --text/--text-muted/etc.
           en style inline sur <html>, qui l'emporte toujours sur une règle
           :root externe quelle que soit sa spécificité. Même pattern que
           .exp-public dans design-tokens.css. */
        .pp-landing {
          --ink: #0F1117;
          --surface: #1E2433;
          --surface-2: #13151F;
          --hairline: #2D3348;
          --text: #F9FAFB;
          --text-muted: #D1D5DB;
          --text-faint: #6B7280;
          --brand: #22D15B;
          --brand-deep: #14622f;
          --brand-tint: #22d15b1a;
          --amber: #dfa53f;
          --amber-tint: #dfa53f1a;
        }

        html, body { overflow-x: hidden !important; max-width: 100vw !important; }
        .pp-landing h1, .pp-landing h2, .pp-landing h3, .pp-landing .pp-display, .pp-landing button, .pp-landing nav a { font-family: 'Manrope', system-ui, sans-serif; }
        .pp-landing .pp-num { font-variant-numeric: tabular-nums; }

        @keyframes ppRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .pp-landing .pp-hero-rise { animation: ppRise 0.7s cubic-bezier(.2,.7,.3,1) both; }
        .pp-landing .pp-hero-rise.pp-d2 { animation-delay: .08s; }
        .pp-landing .pp-hero-rise.pp-d3 { animation-delay: .16s; }
        @media (prefers-reduced-motion: reduce) { .pp-landing .pp-hero-rise { animation: none; } }

        .pp-landing .pp-btn-ghost:hover { border-color: #40485e !important; background: #222939 !important; }
        .pp-landing .pp-btn-solid:hover { background: #1ebd52 !important; }
        .pp-landing .pp-row:hover { border-color: #394158 !important; }
        .pp-landing .pp-mcard:hover { border-color: #394158 !important; }
        .pp-landing .pp-nav a { position: relative; }
        .pp-landing .pp-nav a:hover { color: var(--text) !important; }

        @media (max-width: 980px) {
          .pp-landing .pp-nav-links { display: none !important; }
          .pp-landing .pp-header-inner, .pp-landing .pp-section { padding-left: 20px !important; padding-right: 20px !important; }
          .pp-landing .pp-hero-grid { grid-template-columns: 1fr !important; }
          .pp-landing .pp-hero-visual { margin-top: 40px; order: 2; }
          .pp-landing .pp-hero-copy { text-align: left !important; align-items: flex-start !important; }
          .pp-landing .pp-hero-title { font-size: 38px !important; }
          .pp-landing .pp-features-grid { grid-template-columns: 1fr !important; }
          .pp-landing .pp-modules-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .pp-landing .pp-plans-grid { grid-template-columns: 1fr !important; }
          .pp-landing .pp-expert-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
        @media (max-width: 640px) {
          .pp-landing .pp-hero-title { font-size: 30px !important; }
          .pp-landing .pp-hero-sub { font-size: 15px !important; }
          .pp-landing .pp-modules-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .pp-landing .pp-header-inner { padding-left: 12px !important; padding-right: 12px !important; }
          .pp-landing .pp-header-buttons button { padding: 7px 12px !important; font-size: 12px !important; }
        }
      `}</style>

      <Chatbot sourcePage="landing" />

      {/* ── Bannière offre de lancement ────────────────────────── */}
      <div style={{ background: "var(--surface-2)", borderBottom: "1px solid var(--hairline)", padding: "9px 24px", textAlign: "center", fontSize: 13, color: "var(--text-muted)" }}>
        <Icon name="spark" size={13} style={{ color: "var(--brand)", verticalAlign: "-2px", marginRight: 6 }} />
        <strong style={{ color: "var(--text)", fontWeight: 600 }}>Offre de lancement :</strong> tarif réduit sur l'engagement annuel — essai gratuit, sans carte bancaire.
      </div>

      {/* ── Header ──────────────────────────────────────────────── */}
      <header style={{ borderBottom: "1px solid var(--hairline)", position: "sticky", top: 0, zIndex: 20, background: "#0F1117ee", backdropFilter: "blur(10px)" }}>
        <div className="pp-header-inner" style={{ maxWidth: 1180, margin: "0 auto", padding: "0 40px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={logoIcon} alt="ExecPro360" style={{ height: 30, width: "auto", display: "block" }} />
            <span style={{ fontSize: 17, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.01em" }}>
              ExecPro<span style={{ color: "var(--brand)" }}>360</span>
            </span>
          </div>
          <nav className="pp-nav-links pp-nav" style={{ display: "flex", gap: 30, fontSize: 14, color: "var(--text-muted)" }}>
            <a href="#features" style={{ color: "inherit", textDecoration: "none" }}>Fonctionnalités</a>
            <a href="#plans" style={{ color: "inherit", textDecoration: "none" }}>Tarifs</a>
            <a href="#expert" style={{ color: "inherit", textDecoration: "none" }}>Experts-comptables</a>
          </nav>
          <div className="pp-header-buttons" style={{ display: "flex", gap: 10 }}>
            <button className="pp-btn-ghost" onClick={onGoToLogin} style={{ background: "none", border: "1px solid var(--hairline)", color: "var(--text)", padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "background .15s, border-color .15s" }}>
              Connexion
            </button>
            <button className="pp-btn-solid" onClick={goToRegister} style={{ background: "var(--brand)", border: "none", color: "#08130c", padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "background .15s" }}>
              Essai gratuit
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="pp-section" style={{ position: "relative", maxWidth: 1180, margin: "0 auto", padding: "76px 40px 40px", overflow: "hidden", contain: "paint" }}>
        <img src={logoLockup} alt="" aria-hidden="true" style={{ position: "absolute", top: "-6%", right: "-8%", height: "150%", width: "auto", opacity: 0.05, pointerEvents: "none", userSelect: "none" }} />
        <div className="pp-hero-grid" style={{ position: "relative", display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 56, alignItems: "center" }}>

          <div className="pp-hero-copy">
            <h1 className="pp-hero-title pp-hero-rise" style={{ fontSize: 48, fontWeight: 800, color: "var(--text)", lineHeight: 1.12, letterSpacing: "-0.02em", margin: "0 0 20px" }}>
              Le pilotage financier et opérationnel de votre entreprise, réuni dans une seule application
            </h1>
            <p className="pp-hero-sub pp-hero-rise pp-d2" style={{ fontSize: 17, color: "var(--text-muted)", maxWidth: 480, lineHeight: 1.65, margin: "0 0 32px" }}>
              Facturation, trésorerie, stocks, CRM et comptabilité. À partir de{" "}
              <span className="pp-num" style={{ color: "var(--text)", fontWeight: 600 }}>{LOWEST_ANNUAL}&nbsp;€&nbsp;HT/mois</span>,
              soit <span className="pp-num">{annualTotal(LOWEST_ANNUAL)}&nbsp;€&nbsp;HT</span> facturés annuellement (engagement 12&nbsp;mois).
            </p>
            <div className="pp-hero-rise pp-d3" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
              <button className="pp-btn-solid" onClick={goToRegister} style={{ background: "var(--brand)", border: "none", color: "#08130c", padding: "13px 26px", borderRadius: 9, fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "background .15s" }}>
                Démarrer l'essai gratuit
              </button>
              <a href="/booking/93a810f8-15d8-49a9-9f7f-09b5257bb299" target="_blank" rel="noreferrer" className="pp-btn-ghost" style={{ background: "none", border: "1px solid var(--hairline)", color: "var(--text)", padding: "13px 26px", borderRadius: 9, fontSize: 15, fontWeight: 600, cursor: "pointer", textDecoration: "none", transition: "background .15s, border-color .15s" }}>
                Réserver une démo
              </a>
            </div>
            <div className="pp-hero-rise pp-d3" style={{ fontSize: 13, color: "var(--text-faint)" }}>
              Sans carte bancaire · Mensuel sans engagement, résiliable à tout moment · Hébergé en UE
            </div>
            <div className="pp-hero-rise pp-d3" style={{ fontSize: 13, color: "var(--text-faint)", marginTop: 6 }}>
              Pas encore prêt à réserver ?{" "}
              <span onClick={() => setShowDemoModal(true)} style={{ color: "var(--brand)", cursor: "pointer" }}>
                Laissez-nous vos coordonnées
              </span>
            </div>
          </div>

          {/* Aperçu tableau de bord — ancre visuelle concrète, pas décorative */}
          <div className="pp-hero-visual pp-hero-rise pp-d2">
            <div style={{ background: "var(--surface)", border: "1px solid var(--hairline)", borderRadius: 14, padding: 22, boxShadow: "0 24px 60px -20px #00000066" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                <span style={{ fontSize: 12, color: "var(--text-faint)" }}>Tableau de bord · Septembre 2026</span>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--brand)", display: "inline-block" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                <div style={{ background: "var(--surface-2)", border: "1px solid var(--hairline)", borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 6 }}>Chiffre d'affaires</div>
                  <div className="pp-num" style={{ fontSize: 22, fontWeight: 800, color: "var(--text)" }}>48 210 €</div>
                  <div style={{ fontSize: 11, color: "var(--brand)", marginTop: 4 }}>+12,4&nbsp;% vs août</div>
                </div>
                <div style={{ background: "var(--surface-2)", border: "1px solid var(--hairline)", borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 6 }}>Trésorerie</div>
                  <div className="pp-num" style={{ fontSize: 22, fontWeight: 800, color: "var(--text)" }}>112 640 €</div>
                  <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>3 comptes reliés</div>
                </div>
              </div>
              <div style={{ background: "var(--surface-2)", border: "1px solid var(--hairline)", borderRadius: 10, padding: "14px 14px 8px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-faint)", marginBottom: 10 }}>
                  <span>Encaissements — 30 derniers jours</span>
                </div>
                <svg viewBox="0 0 260 56" width="100%" height="56" preserveAspectRatio="none">
                  <polyline points="0,44 30,38 60,40 90,26 120,30 150,18 180,22 210,10 240,14 260,6"
                    fill="none" stroke="#22d15b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, fontSize: 11, color: "var(--text-faint)" }}>
                <span>7 factures en attente</span>
                <span>2 relances programmées</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────── */}
      <section id="features" className="pp-section" style={{ maxWidth: 1180, margin: "0 auto", padding: "88px 40px" }}>
        <div style={{ maxWidth: 480, marginBottom: 44 }}>
          <h2 style={{ fontSize: 30, fontWeight: 800, color: "var(--text)", marginBottom: 10, letterSpacing: "-0.01em" }}>Tout ce dont une PME a besoin</h2>
          <p style={{ fontSize: 15, color: "var(--text-muted)", lineHeight: 1.6 }}>Une suite complète, pensée pour les entreprises françaises.</p>
        </div>
        <div className="pp-features-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 48 }}>
          {FEATURES.map((f, i) => (
            <div key={f.title} className="pp-row" style={{ display: "flex", gap: 16, padding: "22px 0", borderTop: i < 2 ? "1px solid var(--hairline)" : "1px solid var(--hairline)", transition: "border-color .15s" }}>
              <div style={{ width: 38, height: 38, borderRadius: 9, background: "var(--brand-tint)", color: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={f.icon} size={19} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 5 }}>{f.title}</div>
                <div style={{ fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.6, maxWidth: 400 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Tarifs : 3 forfaits fixes ─────────────────────────────── */}
      <section id="plans" className="pp-section" style={{ background: "var(--surface-2)", borderTop: "1px solid var(--hairline)", borderBottom: "1px solid var(--hairline)", padding: "88px 40px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h2 style={{ fontSize: 30, fontWeight: 800, color: "var(--text)", marginBottom: 10, letterSpacing: "-0.01em" }}>Des forfaits clairs, pensés pour grandir avec vous</h2>
            <p style={{ fontSize: 15, color: "var(--text-muted)" }}>Sans frais cachés. Changez de forfait à tout moment. Tous les prix sont affichés HT.</p>
          </div>

          {/* Toggle mensuel / annuel */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 40 }}>
            <div style={{ display: "inline-flex", background: "var(--ink)", border: "1px solid var(--hairline)", borderRadius: 10, padding: 4 }}>
              <button
                onClick={() => setBillingCycle('monthly')}
                aria-pressed={billingCycle === 'monthly'}
                style={{ padding: "8px 22px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, background: billingCycle === 'monthly' ? "var(--brand)" : "transparent", color: billingCycle === 'monthly' ? "#08130c" : "var(--text-muted)", transition: "background .15s, color .15s" }}
              >
                Mensuel
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                aria-pressed={billingCycle === 'annual'}
                style={{ padding: "8px 22px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, background: billingCycle === 'annual' ? "var(--brand)" : "transparent", color: billingCycle === 'annual' ? "#08130c" : "var(--text-muted)", transition: "background .15s, color .15s" }}
              >
                Annuel
              </button>
            </div>
          </div>

          {/* 3 forfaits */}
          <div className="pp-plans-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, alignItems: "stretch" }}>
            {PLANS.map(p => (
              <div key={p.key} style={{ position: "relative", background: "var(--surface)", border: p.recommended ? "2px solid var(--brand)" : "1px solid var(--hairline)", borderRadius: 14, padding: 28, display: "flex", flexDirection: "column" }}>
                {p.recommended && (
                  <span style={{ position: "absolute", top: -12, left: 28, background: "var(--brand)", color: "#08130c", fontSize: 11, fontWeight: 800, padding: "4px 12px", borderRadius: 20, letterSpacing: "0.02em" }}>
                    RECOMMANDÉ
                  </span>
                )}
                <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text)", marginBottom: 6 }}>{p.name}</div>
                <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 18, minHeight: 32 }}>{p.tagline}</div>
                <div style={{ marginBottom: 4 }}>
                  <span className="pp-num" style={{ fontSize: 34, fontWeight: 800, color: "var(--text)" }}>{billingCycle === 'annual' ? p.priceAnnual : p.priceMonthly}&nbsp;€</span>
                  <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}> HT/mois</span>
                </div>
                <div className="pp-num" style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                  {billingCycle === 'annual'
                    ? <>soit {annualTotal(p.priceAnnual)}&nbsp;€&nbsp;HT facturés annuellement · engagement 12&nbsp;mois</>
                    : 'facturé mensuellement · sans engagement, résiliable à tout moment'}
                </div>
                <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginBottom: 20 }}>
                  {p.users}
                </div>
                {p.inherits && (
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)", fontWeight: 700, marginBottom: 10 }}>{p.inherits}</div>
                )}
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", display: "flex", flexDirection: "column", gap: 8, flexGrow: 1 }}>
                  {p.features.map(f => (
                    <li key={f} style={{ fontSize: 12.5, color: "var(--text-muted)", display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <span style={{ color: "var(--brand)", flexShrink: 0 }}>✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button className="pp-btn-solid" onClick={goToRegister} style={{ width: "100%", background: p.recommended ? "var(--brand)" : "transparent", border: p.recommended ? "none" : "1px solid var(--hairline)", color: p.recommended ? "#08130c" : "var(--text)", padding: "11px 0", borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: "pointer", transition: "background .15s" }}>
                  Démarrer l'essai gratuit
                </button>
              </div>
            ))}
          </div>

          {/* Add-on société satellite */}
          <div style={{ maxWidth: 620, margin: "24px auto 0", textAlign: "center", background: "var(--ink)", border: "1px solid var(--hairline)", borderRadius: 12, padding: "16px 24px", fontSize: 13, color: "var(--text-muted)" }}>
            <strong className="pp-num" style={{ color: "var(--text)" }}>Société satellite additionnelle</strong>{" "}
            — <strong className="pp-num" style={{ color: "var(--brand)" }}>{billingCycle === 'annual' ? SATELLITE_ADDON.annual : SATELLITE_ADDON.monthly}&nbsp;€ HT/mois</strong>
            {billingCycle === 'annual'
              ? <>, soit <span className="pp-num">{annualTotal(SATELLITE_ADDON.annual)}&nbsp;€&nbsp;HT</span> facturés annuellement (engagement 12&nbsp;mois)</>
              : ' (mensuel, sans engagement, résiliable à tout moment)'}, en complément d'un forfait Ultimate.
          </div>

          {/* Bandeau Enterprise */}
          <div style={{ maxWidth: 900, margin: "20px auto 0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap", background: "var(--surface-2)", border: "1px solid var(--hairline)", borderRadius: 12, padding: "22px 28px" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Enterprise</div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Groupes complexes, SLA, API, SSO, migration et support dédié.</div>
            </div>
            <button className="pp-btn-ghost" onClick={() => setShowDemoModal(true)} style={{ background: "none", border: "1px solid var(--hairline)", color: "var(--text)", padding: "11px 24px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
              Contactez-nous
            </button>
          </div>

          <div style={{ textAlign: "center", fontSize: 13, color: "var(--text-faint)", marginTop: 40 }}>
            Tous les forfaits incluent SSL, sauvegardes automatiques, conformité RGPD et support technique. Prix affichés hors taxes (HT).
          </div>
        </div>
      </section>

      {/* ── Expert-comptable — programme partenaire ──────────────── */}
      <section id="expert" className="pp-section" style={{ padding: "88px 40px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div className="pp-expert-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 52, alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 12.5, color: "var(--amber)", fontWeight: 700, marginBottom: 14 }}>Cabinets d'expertise comptable</div>
              <h2 style={{ fontSize: 27, fontWeight: 800, color: "var(--text)", marginBottom: 14, lineHeight: 1.25, letterSpacing: "-0.01em" }}>
                Expert-comptable ? Un accès dédié à vos clients, sans frais.
              </h2>
              <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 22 }}>
                Nous offrons un accès complet et gratuit à tous les cabinets d'expertise comptable qui utilisent ExecPro360 avec leurs clients — inclus dans tous les forfaits de vos clients, sans surcoût pour eux.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {[
                  ['eye', 'Accès lecture/révision sur tous vos clients ExecPro360'],
                  ['award', 'Badge officiel « Partenaire Expert-Comptable » sur votre profil'],
                  ['refresh', 'Gratuit tant que votre client reste actif sur la plateforme — aucune carte bancaire, aucun engagement'],
                ].map(([icon, text]) => (
                  <div key={text} style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)" }}>
                    <span style={{ color: "var(--amber)", flexShrink: 0, marginTop: 1 }}><Icon name={icon} size={16} /></span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Formulaire de demande partenariat */}
            <div style={{ background: "var(--surface)", border: "1px solid var(--hairline)", borderRadius: 13, padding: 26 }}>
              {partnerSent ? (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <div style={{ color: "var(--brand)", marginBottom: 14, display: "flex", justifyContent: "center" }}>
                    <Icon name="userCheck" size={30} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 8 }}>Demande envoyée</div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Notre équipe vous contactera sous 48h pour activer votre accès partenaire.</div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--text)", marginBottom: 18 }}>Demander l'accès partenaire</div>
                  {[
                    ['Votre nom', 'name', 'text', 'Ex : Jean Dupont'],
                    ['Email professionnel', 'email', 'email', 'jean.dupont@cabinet.fr'],
                    ['Nom du cabinet', 'cabinet', 'text', 'Ex : Cabinet Dupont & Associés'],
                    ['Nombre de clients (approx.)', 'clients', 'number', 'Ex : 50'],
                  ].map(([label, key, type, placeholder]) => (
                    <div key={key} style={{ marginBottom: 13 }}>
                      <label style={{ fontSize: 11.5, color: "var(--text-muted)", display: "block", marginBottom: 5 }}>{label}</label>
                      <input
                        type={type}
                        placeholder={placeholder}
                        value={partnerForm[key]}
                        onChange={e => setPartnerForm(f => ({ ...f, [key]: e.target.value }))}
                        style={{ width: "100%", background: "var(--surface-2)", border: "1px solid var(--hairline)", borderRadius: 8, padding: "9px 12px", color: "var(--text)", fontSize: 13, boxSizing: "border-box", fontFamily: "inherit" }}
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => { if (partnerForm.name && partnerForm.email && partnerForm.cabinet) setPartnerSent(true); }}
                    style={{ width: "100%", background: "var(--amber)", border: "none", color: "#1a1204", padding: "11px 0", borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: "pointer", marginTop: 6 }}
                  >
                    Demander l'accès gratuit
                  </button>
                  <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--text-faint)", textAlign: "center" }}>
                    Réponse sous 48h · Sans engagement · Aucune CB
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA final ───────────────────────────────────────────── */}
      <section className="pp-section" style={{ borderTop: "1px solid var(--hairline)", padding: "72px 40px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
          <img src={logoIcon} alt="" style={{ height: 34, marginBottom: 22, opacity: 0.9 }} />
          <h2 style={{ fontSize: 27, fontWeight: 800, color: "var(--text)", marginBottom: 14, letterSpacing: "-0.01em" }}>Prêt à simplifier votre gestion financière ?</h2>
          <p style={{ fontSize: 14.5, color: "var(--text-muted)", marginBottom: 26 }}>
            Essai gratuit · À partir de {LOWEST_ANNUAL}&nbsp;€ HT/mois, soit {annualTotal(LOWEST_ANNUAL)}&nbsp;€ HT facturés annuellement (engagement 12&nbsp;mois)
          </p>
          <button className="pp-btn-solid" onClick={goToRegister} style={{ background: "var(--brand)", border: "none", color: "#08130c", padding: "13px 32px", borderRadius: 9, fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "background .15s" }}>
            Commencer l'essai gratuit
          </button>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="pp-section" style={{ borderTop: "1px solid var(--hairline)", padding: "28px 40px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src={logoIcon} alt="ExecPro360" style={{ height: 20 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>ExecPro360</span>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)" }}>
            © {COPYRIGHT_YEAR} ExecPro360 · {PUBLISHER_LABEL} · Hébergé en UE · TLS 1.3
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", display: "flex", alignItems: "center", gap: 14 }}>
            <a href="/mentions-legales" style={{ color: "#9CA3AF", textDecoration: "none", borderBottom: "1px solid var(--hairline)" }}>Mentions légales</a>
            <a href="/confidentialite" style={{ color: "#9CA3AF", textDecoration: "none", borderBottom: "1px solid var(--hairline)" }}>Confidentialité</a>
            <a href="/cgu" style={{ color: "#9CA3AF", textDecoration: "none", borderBottom: "1px solid var(--hairline)" }}>CGU</a>
          </div>
        </div>
      </footer>
      <DemoRequestModal open={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </div>
  );
}
